import { AnimatePresence, motion } from 'framer-motion';
import { Link2, Share2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface ShareMenuProps {
  isOpen: boolean;
  onToggle: () => void;
  onShareLinkedIn: () => void;
  onShareTwitter: () => void;
  onShareWhatsApp: () => void;
  onCopyLink: () => void;
}

function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function TwitterIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
    </svg>
  );
}

const menuItems = [
  {
    key: 'linkedin',
    icon: LinkedInIcon,
    label: 'LinkedIn',
    hoverBg: 'hover:bg-[#0A66C2]/5',
    hoverText: 'hover:text-[#0A66C2]',
  },
  {
    key: 'twitter',
    icon: TwitterIcon,
    label: 'X (Twitter)',
    hoverBg: 'hover:bg-[#1D9BF0]/5',
    hoverText: 'hover:text-[#000000]',
  },
  {
    key: 'whatsapp',
    icon: WhatsAppIcon,
    label: 'WhatsApp',
    hoverBg: 'hover:bg-[#25D366]/5',
    hoverText: 'hover:text-[#25D366]',
  },
] as const;

export function ShareMenu({
  isOpen,
  onToggle,
  onShareLinkedIn,
  onShareTwitter,
  onShareWhatsApp,
  onCopyLink,
}: ShareMenuProps) {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);

  const handlers: Record<string, () => void> = {
    linkedin: onShareLinkedIn,
    twitter: onShareTwitter,
    whatsapp: onShareWhatsApp,
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onToggle();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggle]);

  return (
    <div className="relative" ref={menuRef}>
      <div className="flex flex-col items-center gap-1 group">
        <button
          type="button"
          onClick={onToggle}
          className={`w-11 h-11 rounded-lg border flex items-center justify-center transition-all duration-200
            ${isOpen ? 'border-[#E94E1B] text-[#E94E1B]' : 'border-[#CAC7C7] text-[#888185] hover:text-[#2E2A23] hover:border-[#888185] bg-transparent'}`}
          aria-label={t('content.share')}
          aria-expanded={isOpen}
        >
          <Share2
            className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'scale-110' : 'group-active:scale-95'}`}
          />
        </button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-52 bg-white rounded-xl shadow-xl border border-[#CAC7C7] overflow-hidden origin-bottom"
          >
            <div className="p-1.5">
              <p className="px-3 pt-2 pb-1.5 text-[10px] font-semibold tracking-wider uppercase text-[#888185]">
                {t('content.shareOn')}
              </p>

              {menuItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#2E2A23] transition-colors ${item.hoverBg} ${item.hoverText}`}
                  onClick={() => {
                    handlers[item.key]();
                    onToggle();
                  }}
                >
                  <item.icon className="w-4 h-4" />
                  <span className="font-medium">{item.label}</span>
                </button>
              ))}

              <div className="my-1 mx-2 border-t border-[#CAC7C7]" />

              <button
                type="button"
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#2E2A23] hover:bg-[#F4F2F2] transition-colors"
                onClick={() => {
                  onCopyLink();
                  onToggle();
                }}
              >
                <Link2 className="w-4 h-4 text-[#888185]" />
                <span className="font-medium">{t('content.copyLink')}</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
